import { EvaluateFeatureHook, EvaluateFeatureHookContext, useLogger } from "@amzn/xb-hooks";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useConfig, useUserId } from "../../hooks";
import { USE_EVALUATE_ABORT_ERROR_REASON } from "../../utils/constants";

/**
 * TODO add override via XBOS debug tooling
 *   https://tasks.awstcprojects.com/browse/PLATFORM-4625
 */

export interface EvaluateFeatureProviderConfig {
  evaluateFeatureByProxyEndpoint: string;
}

export const useEvaluateFeature: EvaluateFeatureHook = ({ feature, project, fallback, namespace }) => {
  const { log } = useLogger();

  const [returnValue, setReturnValue] = useState<ReturnType<EvaluateFeatureHook>>([fallback, true]);
  const [isLoading, setIsLoading] = useState(true);
  const entityId = useUserId();
  const { evaluateFeatureByProxyEndpoint } = useConfig();
  const abortController = useRef<AbortController | null>(null);
  // clients may add trailing forward-slash in their config
  const evidentlyEndpoint = evaluateFeatureByProxyEndpoint.endsWith("/")
    ? evaluateFeatureByProxyEndpoint.slice(0, -1)
    : evaluateFeatureByProxyEndpoint;
  const pathParameters = ["projects", project, "evaluations", feature];
  const url = namespace
    ? [evidentlyEndpoint, namespace, ...pathParameters].join("/")
    : [evidentlyEndpoint, ...pathParameters].join("/");

  useEffect(() => {
    if (isLoading) {
      abortController.current = new AbortController();

      fetch(url, {
        signal: abortController.current.signal,
        method: "POST",
        body: JSON.stringify({ entityId }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          if (typeof fallback === "boolean") {
            return data?.value?.boolValue;
          } else if (typeof fallback === "string") {
            return data?.value?.stringValue;
          } else if (typeof fallback === "number") {
            return data?.value?.doubleValue ?? data?.value?.longValue;
          }
        })
        .then((val) => {
          if (val === undefined) {
            log.fatal("Evaluated featured did not return the expected type");
            return fallback;
          }
          return val;
        })
        .then((val) => {
          setReturnValue([val, false]);
          setIsLoading(false);
        })
        .catch((reason: unknown) => {
          // Avoid preventing new fetch requests if we aborted the fetch ourselves
          if (reason === USE_EVALUATE_ABORT_ERROR_REASON) {
            return;
          }

          log.fatal(
            `Failed to evaluate feature: feature:${feature}, project:${project}`,
            reason instanceof Error ? reason : undefined,
          );
          setReturnValue([fallback, false]);
          setIsLoading(false);
        });
    }
    return () => {
      abortController.current?.abort(USE_EVALUATE_ABORT_ERROR_REASON);
    };
  }, [isLoading, url, project, feature, fallback, entityId, log]);

  return returnValue;
};

export const EvaluateFeatureHookProvider = ({ children }: { children: ReactNode }) => {
  return (
    <EvaluateFeatureHookContext.Provider value={useEvaluateFeature}>{children}</EvaluateFeatureHookContext.Provider>
  );
};
